<template>
  <NuxtLayout name="standard">
    <template v-if="strapiPageData">
      <ConnectifTag :name="CONNECTIF_PAGE_TAGS.OTHERS" />
      <ConnectifTag :name="CONNECTIF_CONTENT_TAGS.OTHERS" />

      <SHeroSearch
        :image="strapiPageData.attributes.hero.image.data.attributes.url"
        :title="t('landings.escapadas-branding.hero.title')"
        :badge="strapiPageData.attributes.hero.author"
      ></SHeroSearch>
      <SSection
        v-if="strapiPageData.attributes.intro_seo_text"
        :title="strapiPageData.attributes.intro_seo_text.title"
        :has-separator="false"
        :full-width="false"
        :centered="true"
      >
        <SShortDescription
          :text="strapiPageData.attributes.intro_seo_text.text"
        />
      </SSection>
      <SSection
        v-if="strapiPageData.attributes.image_banner"
        :has-separator="false"
        :mobile-full="true"
      >
        <SCtaCard v-bind="imageBanner" />
      </SSection>
      <SCardsImagesWrapper
        v-if="strapiPageData.attributes.featured_destinations"
        v-bind="featuredDestinations"
      />
      <SSpacer />
    </template>
  </NuxtLayout>
</template>

<script setup lang="ts">
// Constants
import {
  CONNECTIF_PAGE_TAGS,
  CONNECTIF_CONTENT_TAGS,
} from '~~/modules/integrations/connectif/constants'
import { sendConnectifPage } from '~~/modules/integrations/connectif/composables/useConnectifComposable'
import { MenuPublicHome } from '~~/modules/menu/constants/public-menu'

// Types
import { type schemaType as brandingCampaignPageSchemaType } from '~~/modules/api/strapi/branding-campaign/schema'

// Composables
import { useHeadInfoComposable } from '~~/modules/head/composables/useHeadInfoComposable'

// Transformers
import { useToSCardsImagesWrapper } from '~/composables/transformers/useToSCardsImagesWrapper'
import { useToSCtaCard } from '~/composables/transformers/useToSCtaCard'

// Components Props
import type { propsInterface as sCardsImagesWrapperProps } from '~~/modules/design-system/sections/s-cards-images/s-cards-images-wrapper.vue'
import type { propsInterface as sCtaCardProps } from '~~/modules/design-system/sections/s-cta-card/s-cta-card.vue'

// Apis
import {
  serverFetch as getPageData,
  type returnType as pageDataType,
} from '~~/modules/api/strapi/branding-campaign'

definePageMeta({
  publicMenuItem: MenuPublicHome,
})

// Composables

const { setHeadInfo } = useHeadInfoComposable()

// Methods
const showErrorPage = (error: ApiResponseError) => {
  throw createError({
    statusCode: 503,
    message: error.message,
    fatal: true,
  })
}

const rawStrapiPageData: pageDataType = await getPageData()

let strapiPageData: brandingCampaignPageSchemaType
let imageBanner: sCtaCardProps
let featuredDestinations: sCardsImagesWrapperProps

if (rawStrapiPageData.success && rawStrapiPageData.success.data) {
  strapiPageData = rawStrapiPageData.success.data

  if (strapiPageData) {
    setHeadInfo({
      title: t('landings.escapadas-branding.seo.title'),
      description: t('landings.escapadas-branding.seo.description'),
      images: [
        {
          url: strapiPageData.attributes.hero.image.data.attributes.url,
          alt: t('landings.escapadas-branding.seo.image-alt'),
        },
      ],
    })

    if (strapiPageData.attributes.image_banner) {
      const imageBannerData =
        useToSCtaCard().fromStrapiBrandingCampaign(strapiPageData)
      if (imageBannerData) {
        imageBanner = imageBannerData
      }
    }

    if (strapiPageData.attributes.featured_destinations) {
      featuredDestinations =
        useToSCardsImagesWrapper().fromStrapiBrandingCampaign(strapiPageData)
    }
  }
} else if (rawStrapiPageData.error) {
  showErrorPage(rawStrapiPageData.error as ApiResponseError)
}

sendConnectifPage()
</script>
